// Version Management Service
class VersionManager {
  constructor(api) {
    this.api = api;
    this.currentVersion = null;
    this.serverVersion = null;
    this.checkInterval = 5 * 60 * 1000; // Check every 5 minutes
    this.intervalId = null;
    this.isChecking = false;
    this.pushManager = null; // Will be set from main.js
  }

  // Set push manager reference
  setPushManager(pushManager) {
    this.pushManager = pushManager;
  }

  // Initialize version checking
  async initialize() {
    try {
      console.log('[VERSION] Initializing version manager...');
      
      // Load current local version
      await this.loadLocalVersion();
      
      // Check server version
      await this.checkServerVersion();
      
      // Start periodic checking
      this.startPeriodicCheck();
      
      console.log('[VERSION] Version manager initialized successfully');
      return true;
    } catch (error) {
      console.error('[VERSION] Failed to initialize version manager:', error);
      return false;
    }
  }

  // Load current local version
  async loadLocalVersion() {
    try {
      const response = await fetch('/version.json?t=' + Date.now(), {
        cache: 'no-store'
      });
      if (response.ok) {
        this.currentVersion = await response.json();
        console.log('[VERSION] Loaded local version:', this.currentVersion);
      } else {
        console.warn('[VERSION] Could not load local version, using fallback');
        this.currentVersion = {
          version: '1.0.0',
          buildNumber: 1,
          timestamp: new Date().toISOString()
        };
      }
    } catch (error) {
      console.error('[VERSION] Error loading local version:', error);
      this.currentVersion = {
        version: '1.0.0',
        buildNumber: 1,
        timestamp: new Date().toISOString()
      };
    }
  }

  // Check server version
  async checkServerVersion() {
    if (this.isChecking) return;
    
    this.isChecking = true;
    try {
      console.log('[VERSION] Checking server version...');
      
      const response = await this.api.get('api/version');
      if (response && response.frontendVersion) {
        this.serverVersion = response.frontendVersion;
        console.log('[VERSION] Server version:', this.serverVersion);
        
        // Compare versions
        const needsUpdate = this.compareVersions();
        if (needsUpdate) {
          console.log('[VERSION] Update needed!');
          await this.handleVersionUpdate();
        } else {
          console.log('[VERSION] Application is up to date');
        }
        
        return needsUpdate;
      } else {
        console.warn('[VERSION] Invalid server version response');
        return false;
      }
    } catch (error) {
      console.error('[VERSION] Failed to check server version:', error);
      return false;
    } finally {
      this.isChecking = false;
    }
  }

  // Compare local and server versions
  compareVersions() {
    if (!this.currentVersion || !this.serverVersion) {
      return false;
    }

    // Compare build numbers (more precise than version strings)
    const currentBuild = this.currentVersion.buildNumber || 0;
    const serverBuild = this.serverVersion.buildNumber || 0;
    
    console.log('[VERSION] Comparing versions:');
    console.log(`  Current: ${this.currentVersion.version} (build ${currentBuild})`);
    console.log(`  Server: ${this.serverVersion.version} (build ${serverBuild})`);
    
    return serverBuild > currentBuild;
  }

  // Handle version update
  async handleVersionUpdate() {
    try {
      console.log('[VERSION] Handling version update...');
      
      // Show update notification to user
      const shouldUpdate = await this.showUpdatePrompt();
      
      if (shouldUpdate) {
        await this.performUpdate();
      } else {
        console.log('[VERSION] User declined update');
        // Check again in 1 hour if user declined
        setTimeout(() => this.checkServerVersion(), 60 * 60 * 1000);
      }
    } catch (error) {
      console.error('[VERSION] Error handling version update:', error);
    }
  }

  // Show update prompt to user
  async showUpdatePrompt() {
    return new Promise((resolve) => {
      // Create update notification
      const notification = document.createElement('div');
      notification.className = 'version-update-notification';
      notification.innerHTML = `
        <div class="version-update-content">
          <div class="version-update-header">
            <div class="version-update-icon">🔄</div>
            <div class="version-update-title">Доступно обновление</div>
          </div>
          <div class="version-update-body">
            <p>Доступна новая версия приложения ${this.serverVersion.version}.</p>
            <p>Обновление улучшит производительность и добавит новые функции.</p>
          </div>
          <div class="version-update-actions">
            <button class="btn-secondary" onclick="this.closest('.version-update-notification').handleDecline()">
              Позже
            </button>
            <button class="btn-primary" onclick="this.closest('.version-update-notification').handleUpdate()">
              Обновить сейчас
            </button>
          </div>
        </div>
      `;

      // Add event handlers
      notification.handleUpdate = () => {
        document.body.removeChild(notification);
        resolve(true);
      };

      notification.handleDecline = () => {
        document.body.removeChild(notification);
        resolve(false);
      };

      // Add styles
      const style = document.createElement('style');
      style.textContent = `
        .version-update-notification {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          padding: 20px;
        }
        .version-update-content {
          background: white;
          border-radius: 12px;
          padding: 24px;
          max-width: 400px;
          width: 100%;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
        }
        .version-update-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }
        .version-update-icon {
          font-size: 24px;
        }
        .version-update-title {
          font-size: 18px;
          font-weight: 600;
          color: #111;
        }
        .version-update-body {
          margin-bottom: 24px;
          color: #666;
          line-height: 1.5;
        }
        .version-update-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }
        .btn-secondary, .btn-primary {
          padding: 10px 20px;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          border: none;
          transition: all 0.2s;
        }
        .btn-secondary {
          background: #f5f5f5;
          color: #666;
        }
        .btn-secondary:hover {
          background: #eee;
        }
        .btn-primary {
          background: #ace715;
          color: #111;
        }
        .btn-primary:hover {
          background: #9bd613;
        }
        @media (prefers-color-scheme: dark) {
          .version-update-content {
            background: #1a1a1a;
            color: #fff;
          }
          .version-update-title {
            color: #fff;
          }
          .version-update-body {
            color: #aaa;
          }
          .btn-secondary {
            background: #333;
            color: #aaa;
          }
          .btn-secondary:hover {
            background: #444;
          }
        }
      `;

      document.head.appendChild(style);
      document.body.appendChild(notification);
    });
  }

  // Perform the update with push subscription preservation
  async performUpdate() {
    try {
      console.log('[VERSION] Starting application update...');
      
      // Save current push subscription status before update
      let pushStatus = null;
      if (this.pushManager) {
        pushStatus = this.pushManager.getSubscriptionStatus();
        console.log('[VERSION] Saving push subscription status:', pushStatus);
        // Store push status to survive the reload
        localStorage.setItem('__ewert_push_preserve', JSON.stringify({
          status: pushStatus,
          timestamp: Date.now()
        }));
      }
      
      // Show loading indicator
      this.showUpdateProgress();
      
      // Clear all caches
      await this.clearAllCaches();
      
      // Preserve session data
      this.preserveSession();
      
      await this.refreshServiceWorker();

      // Force navigation through a unique URL so iOS PWA does not reuse the old app shell.
      console.log('[VERSION] Reloading application...');
      this.reloadWithCacheBust();
      
    } catch (error) {
      console.error('[VERSION] Error performing update:', error);
      this.hideUpdateProgress();
      alert('Ошибка при обновлении приложения. Попробуйте обновить страницу вручную.');
    }
  }

  // Ask the browser to fetch the latest service worker before navigating.
  async refreshServiceWorker() {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    try {
      const registrations = await navigator.serviceWorker.getRegistrations();

      await Promise.all(registrations.map(async (registration) => {
        try {
          await registration.update();

          if (registration.waiting) {
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          }
        } catch (error) {
          console.warn('[VERSION] Failed to refresh service worker:', error);
        }
      }));
    } catch (error) {
      console.warn('[VERSION] Failed to get service worker registrations:', error);
    }
  }

  // Deprecated force reload flags are ignored by iOS Safari, so use URL cache busting.
  reloadWithCacheBust() {
    const url = new URL(window.location.href);
    url.searchParams.set('_ewert_update', Date.now().toString());
    window.location.replace(url.toString());
  }

  // Show update progress
  showUpdateProgress() {
    const progress = document.createElement('div');
    progress.id = 'version-update-progress';
    progress.innerHTML = `
      <div class="update-progress-content">
        <div class="update-spinner"></div>
        <div class="update-message">Обновление приложения...</div>
      </div>
    `;

    const style = document.createElement('style');
    style.textContent = `
      #version-update-progress {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.9);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10001;
        color: white;
      }
      .update-progress-content {
        text-align: center;
      }
      .update-spinner {
        width: 40px;
        height: 40px;
        border: 3px solid rgba(172, 231, 21, 0.3);
        border-top: 3px solid #ace715;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 0 auto 16px;
      }
      .update-message {
        font-size: 16px;
        font-weight: 500;
      }
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;

    document.head.appendChild(style);
    document.body.appendChild(progress);
  }

  // Hide update progress
  hideUpdateProgress() {
    const progress = document.getElementById('version-update-progress');
    if (progress) {
      progress.remove();
    }
  }

  // Clear all application caches
  async clearAllCaches() {
    console.log('[VERSION] Clearing application caches...');
    
    try {
      // Clear Service Worker caches
      if ('serviceWorker' in navigator && 'caches' in window) {
        const cacheNames = await caches.keys();
        console.log('[VERSION] Found caches:', cacheNames);
        
        await Promise.all(
          cacheNames.map(cacheName => {
            console.log('[VERSION] Deleting cache:', cacheName);
            return caches.delete(cacheName);
          })
        );
        console.log('[VERSION] Service Worker caches cleared');
      }

      // Clear browser storage (except session-critical data)
      if (typeof Storage !== 'undefined') {
        // Don't clear authentication tokens and critical session data
        const preserveKeys = ['token', 'refreshToken', 'user', 'theme', 'pwa-install-dismissed'];
        const localStorage_backup = {};
        
        // Create backup of all critical data
        preserveKeys.forEach(key => {
          const value = localStorage.getItem(key);
          if (value) {
            localStorage_backup[key] = value;
            console.log(`[VERSION] Preserving localStorage key: ${key}`);
          }
        });
        
        // Also preserve any keys that might contain auth tokens
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.includes('token') || key.includes('auth') || key.includes('session'))) {
            const value = localStorage.getItem(key);
            if (value && !localStorage_backup[key]) {
              localStorage_backup[key] = value;
              console.log(`[VERSION] Preserving additional auth key: ${key}`);
            }
          }
        }
        
        // Create emergency backup in sessionStorage
        try {
          sessionStorage.setItem('__ewert_emergency_backup', JSON.stringify(localStorage_backup));
          console.log('[VERSION] Emergency backup created in sessionStorage');
        } catch (e) {
          console.warn('[VERSION] Failed to create emergency backup:', e);
        }
        
        localStorage.clear();
        
        // Restore critical session data
        Object.keys(localStorage_backup).forEach(key => {
          localStorage.setItem(key, localStorage_backup[key]);
        });
        
        console.log('[VERSION] LocalStorage cleared (session preserved)');
      }

      // Clear sessionStorage (non-critical data)
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.clear();
        console.log('[VERSION] SessionStorage cleared');
      }

      return true;
    } catch (error) {
      console.error('[VERSION] Error clearing caches:', error);
      return false;
    }
  }

  // Preserve critical session data
  preserveSession() {
    try {
      const sessionData = {
        token: localStorage.getItem('token'),
        user: localStorage.getItem('user'),
        theme: localStorage.getItem('theme'),
        timestamp: Date.now()
      };

      // Store in a special key that survives the update
      localStorage.setItem('__ewert_session_preserve', JSON.stringify(sessionData));
      console.log('[VERSION] Session data preserved');
    } catch (error) {
      console.error('[VERSION] Error preserving session:', error);
    }
  }

  // Restore preserved session data (called after reload)
  restoreSession() {
    try {
      let restored = false;
      
      // Try to restore from main session preserve
      const preserved = localStorage.getItem('__ewert_session_preserve');
      if (preserved) {
        const sessionData = JSON.parse(preserved);
        
        // Restore session data
        if (sessionData.token) localStorage.setItem('token', sessionData.token);
        if (sessionData.user) localStorage.setItem('user', sessionData.user);
        if (sessionData.theme) localStorage.setItem('theme', sessionData.theme);
        
        // Clean up
        localStorage.removeItem('__ewert_session_preserve');
        
        console.log('[VERSION] Session data restored from main preserve');
        restored = true;
      }
      
      // Try to restore from emergency backup in sessionStorage
      if (!restored) {
        try {
          const emergencyBackup = sessionStorage.getItem('__ewert_emergency_backup');
          if (emergencyBackup) {
            const backupData = JSON.parse(emergencyBackup);
            
            // Restore all keys from backup
            Object.keys(backupData).forEach(key => {
              if (backupData[key]) {
                localStorage.setItem(key, backupData[key]);
                console.log(`[VERSION] Restored from emergency backup: ${key}`);
              }
            });
            
            // Clean up
            sessionStorage.removeItem('__ewert_emergency_backup');
            
            console.log('[VERSION] Session data restored from emergency backup');
            restored = true;
          }
        } catch (e) {
          console.warn('[VERSION] Failed to restore from emergency backup:', e);
        }
      }
      
      // Restore push subscriptions
      this.restorePushSubscriptions();
      
      return restored;
    } catch (error) {
      console.error('[VERSION] Error restoring session:', error);
      return false;
    }
  }
  
  // Restore push subscriptions after update
  async restorePushSubscriptions() {
    try {
      const pushPreserved = localStorage.getItem('__ewert_push_preserve');
      if (pushPreserved && this.pushManager) {
        const pushData = JSON.parse(pushPreserved);
        
        // Only restore if recent (within last 5 minutes)
        if (Date.now() - pushData.timestamp < 5 * 60 * 1000) {
          const { status } = pushData;
          
          console.log('[VERSION] Restoring push subscriptions...', status);
          
          if (status.isSubscribed || status.hasStoredSubscription) {
            // Wait a bit for the app to fully load
            setTimeout(async () => {
              try {
                await this.pushManager.checkAndRestoreSubscription();
                console.log('[VERSION] Push subscriptions restored successfully');
              } catch (error) {
                console.error('[VERSION] Failed to restore push subscriptions:', error);
              }
            }, 2000);
          }
        }
        
        // Clean up
        localStorage.removeItem('__ewert_push_preserve');
      }
    } catch (error) {
      console.error('[VERSION] Error restoring push subscriptions:', error);
    }
  }

  // Start periodic version checking
  startPeriodicCheck() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    this.intervalId = setInterval(() => {
      this.checkServerVersion();
    }, this.checkInterval);

    console.log('[VERSION] Started periodic version checking');
  }

  // Stop periodic version checking
  stopPeriodicCheck() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      console.log('[VERSION] Stopped periodic version checking');
    }
  }

  // Manual version check (can be called from UI)
  async forceCheck() {
    console.log('[VERSION] Manual version check requested');
    return await this.checkServerVersion();
  }

  // Get current version info
  getVersionInfo() {
    return {
      current: this.currentVersion,
      server: this.serverVersion,
      lastChecked: new Date().toISOString()
    };
  }

  // Destroy version manager
  destroy() {
    this.stopPeriodicCheck();
    console.log('[VERSION] Version manager destroyed');
  }
}

export default VersionManager;
